
import {
  GoogleGenAI,
  LiveCallbacks,
  LiveConnectConfig,
  LiveServerMessage,
  LiveSendToolResponseParameters,
  LiveServerToolCall,
  Part,
  Session,
} from "@google/genai";
import { EventEmitter } from "eventemitter3";
import { base64ToArrayBuffer } from "./utils";
import { visualCaptureDiagnostic } from "./visual-capture-diagnostic";
import { buildModelVisualContext, VIEW_360_CONTEXT, type ModelVisualContext } from './model-visual-context';

export interface GeminiClientEventTypes {
  audio: (data: ArrayBuffer) => void;
  close: (event: CloseEvent) => void;
  error: (error: ErrorEvent) => void;
  interrupted: () => void;
  open: () => void;
  setupcomplete: () => void;
  turncomplete: () => void;
  toolcall: (toolCall: LiveServerToolCall) => void;
  thinking: (isThinking: boolean) => void;
  transcription?: (text: string) => void;
}

export class GeminiClient extends EventEmitter<GeminiClientEventTypes> {
  protected client: GoogleGenAI;
  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  private _session: Session | null = null;
  private _model: string | null = null;
  protected config: LiveConnectConfig | null = null;
  private _isThinking: boolean = false;
  private _apiKey: string;
  private _baseUrl: string;
  private _resumptionHandle: string | undefined = undefined;
  private _lastVisualContext = '';

  public get status() {
    return this._status;
  }

  public get resumptionHandle() {
    return this._resumptionHandle;
  }

  public get session() {
    return this._session;
  }

  public get model() {
    return this._model;
  }

  constructor(apiKey: string, idPath?: string, userId?: string | number) {
    super();

    // Resolve backend base URL dynamically
    let baseUrl = process.env.NEXT_PUBLIC_API_URL || "";
    if (baseUrl.startsWith("/")) {
      if (typeof window !== "undefined") {
        baseUrl = `${window.location.protocol}//${window.location.host}${baseUrl}`;
      }
    } else if (!baseUrl && typeof window !== "undefined") {
      baseUrl = `${window.location.protocol}//${window.location.host}`;
    }

    if (idPath) {
      baseUrl = baseUrl.replace(/\/$/, "") + `/project/${idPath}`;
    }
    if (userId) {
      baseUrl = baseUrl.replace(/\/$/, "") + `/user/${userId}`;
    }

    this._apiKey = apiKey;
    this._baseUrl = baseUrl;
    console.log(`[GeminiClient] NEXT_PUBLIC_API_URL="${process.env.NEXT_PUBLIC_API_URL}", resolved baseUrl="${baseUrl}"`);

    this.client = new GoogleGenAI({
      apiKey,
      httpOptions: {
        baseUrl: baseUrl || undefined
      }
    });

    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  async connect(model: string, config: LiveConnectConfig): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      return false;
    }

    this._status = "connecting";
    this._lastVisualContext = '';
    this.config = config;
    this._model = model;

    const connectConfig = { ...config };
    
    // Always pass sessionResumption config so server sends SessionResumptionUpdate
    // messages. When resuming, pass the saved handle; on first connect handle is
    // undefined which tells the server to start a new resumable session.
    if (this._resumptionHandle) {
      console.log(`[GeminiClient] Resuming session with handle: ${this._resumptionHandle}`);
    }
    connectConfig.sessionResumption = {
      ...(connectConfig.sessionResumption || {}),
      handle: this._resumptionHandle
    };
    
    // Enable context window compression with sliding window so sessions can
    // exceed the default 15-minute audio limit. The server automatically
    // compresses older context when approaching the token limit.
    connectConfig.contextWindowCompression = connectConfig.contextWindowCompression || {
      slidingWindow: {}
    };


    const callbacks: LiveCallbacks = {
      onopen: this.onopen,
      onmessage: this.onmessage,
      onerror: this.onerror,
      onclose: this.onclose,
    };

    try {
      this._session = await this.client.live.connect({
        model,
        config: connectConfig,
        callbacks,
      });
    } catch (e) {
      console.error("Error connecting to Gemini Live:", e);
      this._status = "disconnected";
      return false;
    }

    this._status = "connected";
    return true;
  }

  public disconnect() {
    // Clear thinking before closing. A disconnect mid-thought never receives
    // the audio/turncomplete that would otherwise stop thinking, so without this
    // the "thinking" flag (and the avatar's Thinking animation) stays stuck on.
    this.stopThinking();
    if (!this.session) {
      return false;
    }
    this.session?.close();
    this._session = null;
    this._status = "disconnected";
    return true;
  }

  protected onopen() {
    console.log("Connected to Gemini");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    console.error("Gemini error:", e.message, e);
    this._status = "disconnected";
    this._session = null;
    this.emit("error", e);
  }

  protected onclose(e: CloseEvent) {
    console.log("Disconnected from Gemini", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
    });
    this._status = "disconnected";
    this._session = null;
    this.emit("close", e);
  }

  protected async onmessage(message: LiveServerMessage) {
    if (message.setupComplete) {
      console.log("Setup complete");
      this.emit("setupcomplete");
      return;
    }

    if (message.sessionResumptionUpdate) {
      const update = message.sessionResumptionUpdate;
      if (update.newHandle) {
        console.log("Received session resumption handle", update.newHandle);
        this._resumptionHandle = update.newHandle;
      }
      if (update.resumable === false) {
        console.log("Session is no longer resumable");
        this._resumptionHandle = undefined;
      }
    }

    if (message.goAway) {
      console.log("Server will disconnect soon", message.goAway);
    }

    if (message.toolCall) {
      console.log("Tool call received", message.toolCall);
      this.startThinking();
      this._isToolCallPending = true;
      this.emit("toolcall", message.toolCall);
    }

    if (message.serverContent) {
      const { serverContent } = message;

      if ("interrupted" in serverContent) {
        console.log("Interrupted");
        this._isToolCallPending = false;
        this.stopThinking();
        this.emit("interrupted");
        return;
      }

      if ("turnComplete" in serverContent) {
        console.log("Turn complete");
        this._isToolCallPending = false;
        this.stopThinking();
        this.emit("turncomplete");
      }

      if ("outputTranscription" in serverContent && (serverContent as any).outputTranscription?.text) {
        const t = (serverContent as any).outputTranscription.text;
        visualCaptureDiagnostic.recordAgentResponse(t);
        this.emit("transcription", t);
      }

      if ("modelTurn" in serverContent) {
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        parts.forEach((p) => {
          if (p.text) {
            visualCaptureDiagnostic.recordAgentResponse(p.text);
            this.emit("transcription", p.text);
          }
        });

        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);

        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            // Stop thinking when model starts responding
            if (this._isThinking) {
              this.stopThinking();
            }
            this.emit("audio", data);
          }
        });
      }
    }
  }

  private _isToolCallPending: boolean = false;
  private _thinkingTimeout: ReturnType<typeof setTimeout> | null = null;

  public startThinking() {
    if (!this._isThinking) {
      this._isThinking = true;
      console.log("Thinking started");
      this.emit("thinking", true);
    }
    // Reset or start a timeout — auto-stop thinking if Gemini doesn't
    // respond within 10s (prevents stuck thinking from ambient noise triggers)
    // if (this._thinkingTimeout) clearTimeout(this._thinkingTimeout);
    // this._thinkingTimeout = setTimeout(() => {
    //   if (this._isThinking) {
    //     console.warn("[GeminiClient] Thinking timeout (10s) — auto-stopping");
    //     this.stopThinking();
    //   }
    //   this._thinkingTimeout = null;
    // }, 10_000);
  }

  public stopThinking() {
    if (this._thinkingTimeout) {
      clearTimeout(this._thinkingTimeout);
      this._thinkingTimeout = null;
    }
    if (this._isThinking) {
      this._isThinking = false;
      console.log("Thinking stopped");
      this.emit("thinking", false);
    }
  }

  sendToolResponse(toolResponse: LiveSendToolResponseParameters) {
    this._isToolCallPending = false;
    if (!this._session || this._status !== "connected") {
      return;
    }
    this._session.sendToolResponse(toolResponse);
  }

  /**
   * Send realtime audio and/or image input to the Gemini Live session.
   *
   * Routing rules (per Gemini Live API):
   * - `audio/*` chunks → `sendRealtimeInput({ audio })` — continuous stream
   * - `image/*` chunks → `sendRealtimeInput({ video })` — max 1 frame/sec
   *   (rate limiting is the caller's responsibility, e.g. KrpanoCanvasCapture)
   *
   * @param chunks - Array of media chunks (audio or image)
   * @param modelContext - Optional 3D model viewer context (for model viewer source)
   * @param krpanoContextText - Optional pre-built spatial context text (for krpano 360° source)
   */
  sendRealtimeInput(
    chunks: Array<{ mimeType: string; data: string }>,
    modelContext?: ModelVisualContext,
    krpanoContextText?: string,
  ) {
    if (!this._session || this._status !== "connected") {
      return;
    }

    if (this._isToolCallPending) {
      return; // Do not send realtime input while waiting for tool response, it will interrupt the model.
    }

    try {
      for (const ch of chunks) {
        if (ch.mimeType.startsWith("image/")) {
          // Context priority: modelContext (3D viewer) > krpanoContextText (360° spatial) > VIEW_360_CONTEXT (fallback)
          let contextText: string;
          if (modelContext) {
            contextText = buildModelVisualContext(modelContext);
          } else if (krpanoContextText) {
            contextText = krpanoContextText;
          } else {
            contextText = this._lastVisualContext ? VIEW_360_CONTEXT : '';
          }

          if (contextText && contextText !== this._lastVisualContext) {
            this._session.sendRealtimeInput({ text: contextText });
            this._lastVisualContext = contextText;
          }
          // Gemini Live API: image frames are sent via the `video` field
          this._session.sendRealtimeInput({ video: ch });
          visualCaptureDiagnostic.recordSocketSent({
            encodedBytes: ch.data.length,
            mimeType: ch.mimeType,
          });
          console.debug(
            `[GeminiClient] Sent image frame (${ch.mimeType}, ~${Math.round(ch.data.length * 0.75 / 1024)}KB)`,
          );
        } else {
          // Default path: audio chunks (audio/pcm, audio/wav, etc.)
          this._session.sendRealtimeInput({ audio: ch });
        }
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || String(error);
      console.warn("Failed to send realtime input:", errorMessage);
      if (errorMessage.includes("CLOSING") || errorMessage.includes("CLOSED")) {
        this._status = "disconnected";
        this._session = null;
      }
    }
  }

  send(parts: Part | Part[], turnComplete: boolean = true) {
    if (!this._session || this._status !== "connected") {
      return;
    }

    try {
      this._session.sendClientContent({ turns: parts, turnComplete });
    } catch (error: any) {
      const errorMessage = error?.message || error?.toString() || String(error);
      console.warn("Failed to send client content:", errorMessage);
      if (errorMessage.includes("CLOSING") || errorMessage.includes("CLOSED")) {
        this._status = "disconnected";
        this._session = null;
      }
    }
  }

  /**
   * Send a standalone image to the Gemini Live session.
   * Used by capture_full_scene tool to push the panorama frame.
   */
  sendImage(base64Data: string, mimeType: string = 'image/jpeg') {
    if (!this._session || this._status !== "connected") {
      return;
    }
    try {
      this._session.sendRealtimeInput({
        video: { data: base64Data, mimeType }
      });
      console.debug(`[GeminiClient] Sent standalone image (${mimeType}, ~${Math.round(base64Data.length * 0.75 / 1024)}KB)`);
    } catch (error: any) {
      console.warn("Failed to send image:", error?.message || error);
    }
  }
}
